export function getBasename(fileNameOrPath: string, delims: '\\/' | '\\' | '/' = '\\/'): string {
	if (delims === '\\/') {
		return fileNameOrPath.split(/[\\/]/g).pop() ?? '';
	} else if (delims === '/') {
		return fileNameOrPath.split(/\//g).pop() ?? '';
	} else {
		return fileNameOrPath.split(/\\/g).pop() ?? '';
	}
}

export function getExtOfBasename(
	basename: string,
	leadingDotMeansExt = false,
	trailingDotMeansExt = true
): string {
	const pos = basename.lastIndexOf('.');
	if (pos < 0) {
		return '';
	} else if (!leadingDotMeansExt && pos === 0) {
		return '';
	} else if (!trailingDotMeansExt && pos === basename.length - 1) {
		return '';
	}

	return basename.slice(pos);
}

export function getBasenameNoExt(
	fileNameOrPath: string,
	leadingDotMeansExt = false,
	trailingDotMeansExt = true
): string {
	const basename = getBasename(fileNameOrPath);
	const ext = getExtOfBasename(basename, leadingDotMeansExt, trailingDotMeansExt);

	return ext ? basename.slice(0, basename.length - ext.length) : basename;
}

export function getSimpleExt(
	fileNameOrPath: string | null | false | undefined
): string | undefined {
	if (!fileNameOrPath) {
		return;
	}

	const idx = fileNameOrPath.search(/\.[A-Za-z0-9]{0,50}$/);
	return idx >= 0 ? fileNameOrPath.slice(idx) : undefined;
}

export function getSimpleExtLower(
	fileNameOrPath: string | null | false | undefined,
	noDot = false
): string | undefined {
	const ext1 = getSimpleExt(fileNameOrPath)?.toLowerCase();
	return noDot && ext1 ? ext1.slice(1) : ext1;
}

export function getUrlNoQsOrFragment(url: string): string;
export function getUrlNoQsOrFragment(url: string | undefined): string | undefined;
export function getUrlNoQsOrFragment(url: string | undefined): string | undefined {
	if (!url) {
		return;
	}

	const endIdx = url.search(/[?#]/);
	const url2 = endIdx >= 0 ? url.slice(0, endIdx) : url;
	return url2;
}

export function getUrlExt(url: string | undefined): string | undefined {
	if (!url) {
		return;
	}

	const ext = getSimpleExt(getUrlNoQsOrFragment(url));
	if (ext) {
		return decodeURIComponent(ext);
	}
}

export function getUrlBasename(url: string | undefined): string | undefined {
	if (!url) {
		return;
	}

	const noFrag = getUrlNoQsOrFragment(url);
	const parts = noFrag?.split('/');
	// Return the first part
	const basename = parts?.pop();
	if (basename) {
		return decodeURIComponent(basename);
	}
}

export function getExt(
	fileNameOrPath: string,
	leadingDotMeansExt = false,
	trailingDotMeansExt = true
): string {
	return getExtOfBasename(getBasename(fileNameOrPath), leadingDotMeansExt, trailingDotMeansExt);
}

export function normalizeExt(ext: string | undefined | null, keepCase = false): string | undefined {
	if (!ext || !ext.length) {
		return;
	}

	ext = ext.trim();
	if (!ext || !ext.length) {
		return;
	}

	// Ensure ext starts with .
	if (ext[0] !== '.') {
		ext = '.' + ext;
	}

	return keepCase ? ext : ext.toLowerCase();
}

export function extWithDot(ext: string | undefined | null, keepCase = false): string | undefined {
	return normalizeExt(ext, keepCase) ?? '.bin';
}

export function normalizeExtNoDot(
	ext: string | undefined | null,
	keepCase = false
): string | undefined {
	if (!ext || !ext.length) {
		return;
	}

	ext = ext.trim();
	if (!ext || !ext.length) {
		return;
	}

	// Ensure ext doesn't start with .
	while (ext[0] === '.') {
		ext = ext!.slice(1);
	}

	return keepCase ? ext : ext.toLowerCase();
}

export function getNormalizedExt(
	fileNameOrPath: string,
	leadingDotMeansExt = false,
	trailingDotMeansExt = true
): string | undefined {
	return normalizeExt(getExt(fileNameOrPath, leadingDotMeansExt, trailingDotMeansExt));
}

/**
 * If the given extension is already assigned (case insensitively), returns
 * the original string, otherwise assigns the given ext.
 * @param name
 * @param ext
 */
export function ensureExtInsensitive(name: string, ext: string): string {
	const ext2 = normalizeExt(ext);

	if (ext2 && !name.toLowerCase().endsWith(ext2)) {
		return name + ext2;
	}
	return name;
}
